import React, { useEffect } from "react";
import Loader from "../components/Loader";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import Breadcrumbs from "../components/Breadcrumbs";
import { useLocation } from "react-router-dom";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../seo/site";

export default function Terms({ setHeader }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = `${t("header.privacy.pageTitle")} - ${SITE_NAME}`;
  const pageDescription = t("header.privacy.metaDescription");
  const enUrl = `${SITE_URL}/en/terms-of-use`;
  const meUrl = `${SITE_URL}/me/opsti-uslovi-kupovine`;
  const breadcrumbItems = [
    { name: SITE_NAME, item: `${SITE_URL}/${pageLang}` },
    { name: t("header.privacy.pageTitle"), item: pageUrl },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang === "me" ? "sr-ME" : "en" }}>
        {/* <!-- meta tag --> */}
        <meta charSet="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        {/* <!-- Meta Tags--> */}
        <meta
          name="description"
          content={pageDescription}
        />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" href={enUrl} hrefLang="en" />
        <link rel="alternate" href={meUrl} hrefLang="sr-ME" />
        <link rel="alternate" href={meUrl} hrefLang="x-default" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <Loader />
      {/* <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div className="rs-breadcrumbs img3">
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {t("termsAndConditions.title")}
                {/*<span className="watermark">{privacy.title}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <Breadcrumbs bg="light" />
        {/* <!-- Breadcrumbs End --> */}

        {/* <!-- Faq Section Start --> */}
        <div className="rs-faq gray-bg4 pt-120 md-pt-80">
          <div className="container">
            {/* <!-- Privacy policy Section Start --> */}
            <div className="rs-privacy-policy pt-10 md-pt-80">
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">{t("termsAndConditions.sections.introduction.title")}</h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.introduction.content")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                  {t("termsAndConditions.sections.privacyStatement.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.privacyStatement.content")}</p>
                  <p>{t("termsAndConditions.sections.dataProtection.content")}</p>
                  <p>{t("termsAndConditions.sections.userRegistration.content")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.productDescription.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.productDescription.content")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.productPrices.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.productPrices.content_1")}</p>
                  <p>{t("termsAndConditions.sections.productPrices.content_2")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.productAvailability.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.productAvailability.content_1")}</p>
                  <p>{t("termsAndConditions.sections.productAvailability.content_2")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.productDisplay.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.productDisplay.content_1")}</p>
                  <ul className="check-list">
                    <li>{t("termsAndConditions.sections.productDisplay.reason_1")}</li>
                    <li>{t("termsAndConditions.sections.productDisplay.reason_2")}</li>
                  </ul>
                  <p>{t("termsAndConditions.sections.returns.content_2")}</p>
                  <p>{t("termsAndConditions.sections.returns.content_3")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.shippingCosts.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.shippingCosts.content")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.packaging.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.packaging.content")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.paymentMethods.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.paymentMethods.content")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.reclamation.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.reclamation.content_1")}</p>
                  <p>{t("termsAndConditions.sections.reclamation.content_2")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.payback.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.payback.content_1")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.returns.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.returns.content_1")}</p>
                  <p>{t("termsAndConditions.sections.returns.content_2")}</p>
                  <ul className="check-list">
                    <li>{t("termsAndConditions.sections.returns.reason_1")}</li>
                    <li>{t("termsAndConditions.sections.returns.reason_2")}</li>
                    <li>{t("termsAndConditions.sections.returns.reason_3")}</li>
                    <li>{t("termsAndConditions.sections.returns.reason_4")}</li>
                    <li>{t("termsAndConditions.sections.returns.reason_5")}</li>
                    <li>{t("termsAndConditions.sections.returns.reason_6")}</li>
                    <li>{t("termsAndConditions.sections.returns.reason_7")}</li>
                  </ul>
                  <p>{t("termsAndConditions.sections.returns.content_3")}</p>
                  <p>{t("termsAndConditions.sections.returns.content_4")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.vouchers.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.vouchers.content_1")}</p>
                  <p>{t("termsAndConditions.sections.vouchers.content_2")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.changeOfTerms.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("termsAndConditions.sections.changeOfTerms.content_1")}</p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.companyInfo.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p><span className="bold">{t("termsAndConditions.sections.companyInfo.content_1")}</span>
                    <br/>{t("termsAndConditions.sections.companyInfo.content_2")}
                    <br/>{t("termsAndConditions.sections.companyInfo.content_3")}
                    <br/>{t("termsAndConditions.sections.companyInfo.content_4")}
                    <br/>{t("termsAndConditions.sections.companyInfo.content_5")}
                    <br/>{t("termsAndConditions.sections.companyInfo.content_6")}
                    <br/>{t("termsAndConditions.sections.companyInfo.content_7")}
                    <br/>{t("termsAndConditions.sections.companyInfo.content_8")}
                    <br/>{t("termsAndConditions.sections.companyInfo.content_9")}
                  </p>
                </div>
              </div>
              <div className="privacy-wrap pb-60 md-pb-60">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("termsAndConditions.sections.customerService.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p><span className="bold">{t("termsAndConditions.sections.customerService.content_1")}</span>
                    <br/>{t("termsAndConditions.sections.customerService.content_2")}
                    <br/>{t("termsAndConditions.sections.customerService.content_3")}
                    <br/>{t("termsAndConditions.sections.customerService.content_4")}
                    <br/>{t("termsAndConditions.sections.customerService.content_5")}
                  </p>
                </div>
              </div>
            </div>
            {/* <!-- Privacy policy Section End -->    */}
          </div>
          {/* <!-- Newsletter section start --> */}
          <div className="rs-newsletter style1 pt-90 md-pt-60 mb--65">
            <Newsletter />
          </div>
          {/* <!-- Newsletter section end --> */}
        </div>
        {/* <!-- Faq Section End --> */}
      </div>
      {/* <!-- Main content End --> */}
      <StructuredData data={breadcrumbJsonLd(breadcrumbItems)} />
    </>
  );
}

